/**
 * Capacidades (capacidades.html): la arquitectura hexagonal contada como
 * producto — qué sabe hacer la casa, en qué estado está cada pieza y quién
 * la usa. Catálogo de sistema (capacidades.seed.tsx): no hay «vacío» — el
 * contrato lo dice explícitamente — y no hay red real que consultar, así
 * que ?estado= solo fuerza la vista para revisión (como en Conocimiento).
 * DECISIÓN del prototipo: sin sidepane — la cola vive en Operación.
 */
import { Link, useSearch } from "@tanstack/react-router";
import {
  Badge, Banner, buttonClass, Button, Card, CardHeader, CardPad, Fainted, MachineTag, PageHead, Skeleton, Stale, Sub, useToast,
} from "@skein/ui";
import { CAPACIDADES, type Capacidad } from "./capacidades.seed";

function tonoDe(estado: Capacidad["estado"]) {
  return estado === "activa" ? "ok" : estado === "beta" ? "accent" : undefined;
}

function PiezaCard({ c }: { c: Capacidad }) {
  return (
    <Card flush aria-label={c.nombre}>
      <CardHeader title={c.nombre} badge={<Badge tone={tonoDe(c.estado)}>{c.estado}</Badge>} />
      <CardPad prose>
        <p>{c.prosa}</p>
        <div className="tagrow" style={{ marginTop: "var(--sk-sp-3)" }}>
          {c.tags.map((t, i) => <MachineTag key={i} kind={t.kind}>{t.label}</MachineTag>)}
        </div>
        <Sub style={{ marginTop: "var(--sk-sp-3)" }}>
          {c.subPrefijo}
          {c.subLink && <> · <Link to={c.subLink.to} className={buttonClass({ variant: "link" })}>{c.subLink.label}</Link></>}
        </Sub>
      </CardPad>
    </Card>
  );
}

function Cargando() {
  const ln = (w: string, extra?: React.CSSProperties) => <Skeleton style={{ height: ".9em", width: w, ...extra }} />;
  return (
    <section aria-busy="true" aria-label="Cargando capacidades">
      <div className="parrilla">
        <Card>{ln("40%", { marginBottom: "var(--sk-sp-3)" })}{ln("100%")}{ln("70%", { marginTop: "var(--sk-sp-2)" })}</Card>
        <Card>{ln("50%", { marginBottom: "var(--sk-sp-3)" })}{ln("100%")}{ln("60%", { marginTop: "var(--sk-sp-2)" })}</Card>
        <Card>{ln("45%", { marginBottom: "var(--sk-sp-3)" })}{ln("100%")}{ln("65%", { marginTop: "var(--sk-sp-2)" })}</Card>
      </div>
    </section>
  );
}

export function CapacidadesPage() {
  const search = useSearch({ strict: false }) as { estado?: string };
  const say = useToast();
  const forced = search.estado;
  const view = forced === "cargando" || forced === "error" ? forced : "lista";

  if (view === "cargando") return <Cargando />;

  const enError = view === "error";
  const nActivas = CAPACIDADES.filter((c) => c.estado === "activa").length;
  const nBeta = CAPACIDADES.filter((c) => c.estado === "beta").length;
  const nPrevista = CAPACIDADES.filter((c) => c.estado === "prevista").length;

  return (
    <>
      {enError && (
        <Banner tone="danger" style={{ marginBottom: "var(--sk-gap)" }}>
          <b>No llego al estado de las capacidades.</b> Lo que ves puede estar desactualizado. Reintento solo cada 30 s.{" "}
          <Button variant="link" onClick={() => say("Reintentando la conexión…")}>reintentar ahora</Button>
        </Banner>
      )}
      <Stale stale={enError}>
        <PageHead
          title="Capacidades"
          meta={<>
            <Badge>{CAPACIDADES.length} piezas</Badge>
            <Badge tone="ok">{nActivas} activas</Badge>
            <Badge tone="accent">{nBeta} en beta</Badge>
            <Badge>{nPrevista} prevista</Badge>
            <Fainted style={{ fontSize: "var(--sk-fs-2xs)", alignSelf: "center" }}>el asesor crece por piezas — sin reescribir el núcleo</Fainted>
          </>}
        />
        <div className="parrilla">
          {CAPACIDADES.map((c) => <PiezaCard key={c.slug} c={c} />)}
        </div>
      </Stale>
    </>
  );
}
